import React, { useEffect, useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle, Field, Label, Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import api from '../../../api/api';
import { dateToTime } from '../../../utils/Date';

const UpdateEmployeeModal = ({ updateEmployeeModalOpen, setUpdateEmployeeModalOpen, selectedDay, handleUpdateSubmit, selectedEmployee, handleDeleteRoster }) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [assignmentDetails, setAssignmentDetails] = useState({
    employee: null,
    date: '',
    shiftStart: '',
    shiftEnd: '',
    breakTime: '',
  });

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await api.get('/employee/byBusiness');
      const employeeList = response.data.listOfEmployees || [];
      setEmployees(employeeList);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (updateEmployeeModalOpen && selectedEmployee) {
      const employee = employees.find((emp) => emp._id === selectedEmployee.id) || employees[0];
      setSelected(employee);
      setAssignmentDetails({
        employee: employee || selectedEmployee.id,
        date: selectedDay?.date || selectedEmployee.date,
        shiftStart: dateToTime(selectedEmployee.shiftStart),
        shiftEnd: dateToTime(selectedEmployee.shiftEnd),
        breakTime: selectedEmployee.break || '',
      });
    }
  }, [updateEmployeeModalOpen, selectedEmployee, selectedDay, employees]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAssignmentDetails((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = () => {
    const submitDetails = {
      ...assignmentDetails,
      employee: selected || { _id: selectedEmployee.id },
    };
    handleUpdateSubmit(submitDetails);
    setUpdateEmployeeModalOpen(false);
  };

  const onDelete = () => {
    handleDeleteRoster(selectedEmployee);
    setUpdateEmployeeModalOpen(false);
  };

  return (
    <Dialog open={updateEmployeeModalOpen} onClose={setUpdateEmployeeModalOpen} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="w-full relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
              <button
                type="button"
                onClick={() => setUpdateEmployeeModalOpen(false)}
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                <span className="sr-only">Close</span>
                <XMarkIcon aria-hidden="true" className="size-6" />
              </button>
            </div>
            <div>
              <div className="sm:mt-0">
                <DialogTitle as="h3" className="text-center font-semibold text-gray-900">
                  Update roster
                </DialogTitle>
                <div className="mt-2">
                  <form className="space-y-6">
                    <Field>
                      <Label className="block text-sm/6 font-medium text-gray-900">Assign to</Label>
                      <Listbox value={selected} onChange={setSelected} disabled>
                        <div className="relative mt-2">
                          <ListboxButton className="relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:outline-gray-200 sm:text-sm/6">
                            <span className="block truncate">
                              {selected?.firstName} {selected?.lastName}
                            </span>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronUpDownIcon aria-hidden="true" className="size-5 text-gray-400" />
                            </span>
                          </ListboxButton>
                          <ListboxOptions
                            transition
                            className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm"
                          >
                            {employees.map((employee) => (
                              <ListboxOption
                                key={employee._id}
                                value={employee}
                                className="group relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-indigo-600 data-[focus]:text-white"
                              >
                                <span className="block truncate font-normal group-data-[selected]:font-semibold">
                                  {employee.firstName} {employee.lastName}
                                </span>
                                <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600 group-data-[focus]:text-white [.group:not([data-selected])_&]:hidden">
                                  <CheckIcon aria-hidden="true" className="size-5" />
                                </span>
                              </ListboxOption>
                            ))}
                          </ListboxOptions>
                        </div>
                      </Listbox>
                    </Field>
                    <Field>
                      <Label htmlFor="date" className="block text-sm/6 font-medium text-gray-900">Date</Label>
                      <div className="mt-2">
                        <input
                          disabled
                          id="date"
                          name="date"
                          type="date"
                          value={assignmentDetails.date}
                          onChange={handleChange}
                          required
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:outline-gray-200 sm:text-sm/6"
                        />
                      </div>
                    </Field>
                    <Field>
                      <Label htmlFor="shiftStart" className="block text-sm/6 font-medium text-gray-900">Start time</Label>
                      <div className="mt-2">
                        <input
                          id="shiftStart"
                          name="shiftStart"
                          type="time"
                          value={assignmentDetails.shiftStart}
                          onChange={handleChange}
                          required
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm/6"
                        />
                      </div>
                    </Field>
                    <Field>
                      <Label htmlFor="shiftEnd" className="block text-sm/6 font-medium text-gray-900">End time</Label>
                      <div className="mt-2">
                        <input
                          id="shiftEnd"
                          name="shiftEnd"
                          type="time"
                          value={assignmentDetails.shiftEnd}
                          onChange={handleChange}
                          required
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm/6"
                        />
                      </div>
                    </Field>
                    <Field>
                      <Label htmlFor="breakTime" className="block text-sm/6 font-medium text-gray-900">Break (minutes)</Label>
                      <div className="mt-2">
                        <input
                          id="breakTime"
                          name="breakTime"
                          type="number"
                          value={assignmentDetails.breakTime}
                          onChange={handleChange}
                          min="0"
                          step={15}
                          required
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm/6"
                        />
                      </div>
                    </Field>
                  </form>
                </div>
              </div>
            </div>
            <div className="mt-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={onSubmit}
                className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto"
              >
                Confirm
              </button>
              <button
                type="button"
                onClick={onDelete}
                className="mt-3 inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:mt-0 sm:ml-3 sm:w-auto"
              >
                Delete
              </button>
              <button
                type="button"
                onClick={() => setUpdateEmployeeModalOpen(false)}
                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
              >
                Cancel
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default UpdateEmployeeModal;